import React from "react";
import styled from "styled-components";
import CaseStudiesCard from "../CaseStudiesCard";
import Links from "../../../Links";
import Heading from "../../../Heading";
import ImageOne from "../../../../img/casestudy/design-systems/muibook-poster.png";
import ImageBrand from "../../../../img/casestudy/design-systems/muibook-theme-brand.png";
import ImageTwo from "../../../../img/casestudy/design-systems/muibook-tokens.png";
import ImageThree from "../../../../img/casestudy/design-systems/muibook-create-app.png";
import ImageFour from "../../../../img/casestudy/design-systems/muibook-css-parts.png";
import ImageFive from "../../../../img/casestudy/design-systems/muibook-guides.png";
import ImageSix from "../../../../img/casestudy/design-systems/muibook-playground.png";
import Guidelines from "../../../../img/casestudy/design-systems/guidelines.png";
import Figma from "../../../../img/casestudy/design-systems/figma.png";

import Box from "../../../Box";
import Rule from "../../../Rule";
import Stack from "../../../Stack";
import Image from "../../../Image";
import BlogLayout from "../BlogLayout";

const Message = styled.div`
  width: 100%;
  border-radius: 8px;
  padding: var(--spacing-small);
  background: rgba(0 0 0 / 0.2);
  display: flex;
  gap: var(--spacing-x-small);
  && {
    margin-bottom: var(--spacing-medium);
  }
  p {
    margin: 0;
    font-size: var(--font-size-text-small);
  }
`;

const Muibook = (
  <CaseStudiesCard id="muibook">
    <Heading
      variant="smaller"
      style={{ marginTop: 0, marginBottom: "var(--spacing-x-tiny)", textTransform: "uppercase" }}
    >
      ARTICLE
    </Heading>
    <p style={{ marginBottom: 0, fontSize: "var(--font-size-text-medium)" }}>
      A Native, Flexible Design System Built for Trusted Customisation
    </p>
    <Rule spacing="medium" inverted mt="var(--spacing-medium)" mb="var(--spacing-large)" />

    <BlogLayout>
      <Stack spacing="var(--spacing-xx-large)">
        <Box>
          <Heading variant="larger">Muibook</Heading>
          <p>
            <Links href="https://www.muibook.com" target="_blank" rel="noopener noreferrer">
              Muibook.com
            </Links>{" "}
            is the home of the MichaelUI Design System (MUI), a collection of native Web Components built for
            composability, performance and trusted customisation. It helps you write less code with more flexibility and
            empowers teams to build quickly without losing control.
          </p>
          <Message>
            {" "}
            <span role="img" aria-label="Read more on Medium">
              📖
            </span>
            <Stack disableMargin>
              <Heading variant="smallest" mt="0" mb="var(--spacing-tiny)" color="white">
                Continue Reading on Medium
              </Heading>
              <p>
                Thanks for checking out this post. You can also read it on{" "}
                <Links
                  href="https://medium.com/@trilfordm/muibook-a-native-flexible-design-system-built-for-trusted-customisation-3e56c3e2f747"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Medium
                </Links>
                , where it’s part of my design system portfolio. Feel free to leave a comment, share your thoughts, or
                save it for later. I’d love to hear what you think.
              </p>
            </Stack>
          </Message>
          <p>
            Muibook is designed to be framework agnostic, portable and future-friendly. It removes the bloat and
            rigidity found in traditional libraries with no forced conventions and no unnecessary abstractions. Instead,
            it offers clean patterns you can shape to fit your product.
          </p>
          <p>And yes, when we say MUI here, we mean MichaelUI. Not the one owned by Google and their ad empire.</p>
          <Image dark src={ImageOne} alt="muibook.com" />
          <p>
            <b>Fun fact:</b> <i>mui</i> is a Māori word that means “to swarm round, infest, or flock to.” It’s a fitting
            name because, just like great components, a good design system naturally draws people in and becomes a hub
            for creativity and collaboration.
          </p>
        </Box>

        <Box>
          <Heading variant="large">Why I Built This Design System</Heading>
          <p>
            Design systems are hard to demonstrate in a portfolio sense. The work spans many touchpoints, Figma files
            behind logins, coded components inside private Storybooks, and internal docs that rarely see the light of
            day. And even when some of it is public, it’s often just a slice of the full picture.
          </p>
          <p>
            Muibook is my way of showing what I believe a design system should be: not just UI components, but the full
            experience. That means Figma components, code, tokens, UX guidance and AI integration.
          </p>
          <p>
            This is also my chance to highlight the kinds of details that are not easily showcased in portfolios, such
            as:
          </p>
          <ul>
            <li>
              How I handle <b>accessibility</b> across both design and engineering, with live working examples of
              keyboard navigation
            </li>
            <li>
              Usage and understanding of ARIA and role attributes are demonstrated through technical documentation and
              by reviewing the public code repository on GitHub.
            </li>
            <li>
              My approach to <b>multi-brand</b> experiences using CSS variables
            </li>
            <li>
              How I build for <b>performance</b> and <b>composability</b>
            </li>
            <li>
              How I see <b>AI shaping the future</b> of design systems by exposing prompts and keywords through the
              system’s NPM package.
            </li>
          </ul>
          <p>
            I’ve been writing front-end code for years, but I’ve always chased simplicity and clarity. I care deeply
            about how systems perform, how developers use them, and how design decisions flow into code. That’s why I
            chose Web Components over React. They offer native, reusable, framework-agnostic power.
          </p>
          <p>
            This system started years ago as a personal component library. In 2025, I decided to take it further,
            turning my collection of ideas into a full design system. It includes categories like Inputs, Content,
            Layout, Feedback, Actions, and Navigation. Some of my favourite components are the ones that enable
            composability, such as Stack, Responsive, or Slat.
          </p>
          <p>
            Muibook also became a <b>playground</b> for me to explore where this space is going. I created:
          </p>
          <ul>
            <li>
              A <b>demo site</b> with documentation, live examples, and system recipes
            </li>
            <li>
              A <b>Figma UI Kit</b> that mirrors the code
            </li>
            <li>
              A <b>multi-brand theming engine</b> powered by design tokens
            </li>
            <li>
              A <b>part override strategy</b> to support trusted customisation
            </li>
            <li>
              An <b>AI Playground</b>, where the system can generate layouts from prompts
            </li>
            <li>
              And an <b>NPM package</b>, complete with helper tools, AI metadata, and CDN support
            </li>
          </ul>
          <p>
            This project taught me more than I expected, from publishing packages to building AI-aware component
            systems. The prompt and keyword work is just getting started, but I plan to expand it. I want to see if UX
            guidelines can be embedded into AI prompts, helping agents design responsibly, not just quickly.
          </p>
        </Box>
        <Box>
          <Heading variant="large">Why Web Components? </Heading>
          <p>
            In addition to preferring Web Components for their native, reusable, and framework-agnostic qualities, I
            also wanted a styling approach that didn’t rely on runtime CSS compilation via JavaScript. While I love the
            developer experience of libraries like Emotion and Styled Components, they can introduce performance
            overhead that isn’t ideal for every project.
          </p>
          <p>
            Web Components offer the best of both worlds. They provide encapsulated styles through Shadow DOM and expose
            styling hooks via design tokens and ::part selectors. Inside a component, I can write scoped CSS with direct
            access to component props or attributes, enabling logic-driven styling without relying on class toggling or
            external utilities. This approach allows for flexible, dynamic theming without sacrificing performance or
            portability.
          </p>
        </Box>
        <Box>
          <Heading variant="large" style={{ marginBottom: "var(--spacing-x-medium)" }}>
            What I’ve Built So Far
          </Heading>
          <Stack spacing="var(--spacing-xx-large)">
            <Box>
              <Heading variant="medium">
                {" "}
                <span role="img" aria-label="bricks" style={{ marginRight: "var(--spacing-tiny)" }}>
                  🧱
                </span>
                Core System
              </Heading>
              <ul>
                <li>
                  Built <b>30 Web Components</b> focused on composability, flexibility, and a strong developer
                  experience, with consistently applied prop types across components to make adoption easier over time
                </li>
                <li>
                  <b>Encapsulated styles</b> via Shadow DOM ensure visual isolation and prevent unintended style leakage
                  across components
                </li>
                <li>
                  Components grouped across key categories: <b>Inputs</b>, <b>Content</b>, <b>Layout</b>,{" "}
                  <b>Feedback</b>, <b>Actions</b>, and <b>Navigation</b>
                </li>
                <li>
                  <b>Accessibility</b> is built in, with support for <b>ARIA roles</b>, <b>keyboard navigation</b>,{" "}
                  <b>focus management</b>, and <b>screen reader alerts</b>
                </li>
                <li>
                  Included a <b>React Type Element file</b> to enable clean usage in React without TypeScript errors
                </li>
                <li>
                  These components power internal products including{" "}
                  <Links href="https://muibook.com/" target="_blank" rel="noopener noreferrer">
                    muibook.com
                  </Links>
                  ,{" "}
                  <Links href="https://play.muibook.com/" target="_blank" rel="noopener noreferrer">
                    play.muibook.com
                  </Links>
                  ,{" "}
                  <Links href="https://guides.muibook.com/" target="_blank" rel="noopener noreferrer">
                    guides.muibook.com
                  </Links>
                  ,{" "}
                  <Links href="https://create.muibook.com/" target="_blank" rel="noopener noreferrer">
                    create.muibook.com
                  </Links>
                </li>
                <li>
                  The projects mentioned above use either a lightweight <b>vanilla JavaScript</b> setup (via Vite) or
                  the <b>React framework</b>, demonstrating that the system is framework agnostic
                </li>
              </ul>
            </Box>
            <Box>
              <Heading variant="medium">
                {" "}
                <span role="img" aria-label="bricks" style={{ marginRight: "var(--spacing-tiny)" }}>
                  🏗️
                </span>
                Styling Architecture
              </Heading>
              <ul>
                <li>
                  The system includes three core CSS files: <b>Reset</b>, <b>Base</b>, and <b>Tokens</b>, each playing a
                  specific role in ensuring flexibility and consistency
                </li>
                <li>
                  <b>Reset</b> and <b>Base CSS</b> do not pierce the Shadow DOM, and are intended to set up a solid
                  application foundation, providing sensible defaults and reducing browser inconsistencies
                </li>
                <li>
                  The Tokens file (CSS custom properties) can be inherited by Web Components, allowing for{" "}
                  <b>theming</b>, <b>overrides</b>, and <b>trustworthy customisation</b> within a design system
                </li>
              </ul>
            </Box>
            <Box>
              <Heading variant="medium">
                {" "}
                <span role="img" aria-label="bricks" style={{ marginRight: "var(--spacing-tiny)" }}>
                  🧩
                </span>
                Design Tokens
              </Heading>
              <p>
                I spent time carefully considering how tokens fit into each category. While the lines can be fuzzy, the
                real value of these groupings is in helping teams manage and scale theming. The categories aren’t the
                goal. The main objective is enabling consistent, flexible theming. Getting the base mapping right is
                what ensures your themes hold up without breaking the system’s core.
              </p>
              <ul>
                <li>
                  Supports <b>Light</b> and <b>Dark</b> mode theming
                </li>
                <li>
                  Enables <b>brand theming</b> through token customisation
                </li>
                <li>
                  The token system allows <b>visual flexibility</b> without editing the component source
                </li>
                <li>
                  Tokens are structured into:{" "}
                  <Links href="https://muibook.com/#/brand-design-tokens" target="_blank" rel="noopener noreferrer">
                    Brand
                  </Links>
                  ,{" "}
                  <Links href="https://muibook.com/#/semantic-design-tokens" target="_blank" rel="noopener noreferrer">
                    Semantic
                  </Links>
                  ,{" "}
                  <Links
                    href="https://muibook.com/#/contextual-design-tokens"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Contextual
                  </Links>
                  , and{" "}
                  <Links
                    href="https://muibook.com/#/components-design-tokens"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Component-specific
                  </Links>
                </li>

                <li>
                  Includes guidance for teams to <b>customise tokens for different brands</b>
                </li>
                <li>
                  Demo site showcases working examples of <b>Light</b>, <b>Dark</b>, and <b>brand themes</b>
                </li>
                <li>
                  The typography decisions displayed below are informed by my thinking around <b>web typography</b>,
                  which I walk through in the video{" "}
                  <Links
                    href="https://youtu.be/FFGSy0EfA9Y?si=hr4D5JFjqfSSI_rC"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    workshop
                  </Links>
                </li>
              </ul>

              <Image dark src={ImageTwo} alt="Design Tokens" />
            </Box>
            <Box>
              <Heading variant="medium">
                {" "}
                <span role="img" aria-label="bricks" style={{ marginRight: "var(--spacing-tiny)" }}>
                  📘
                </span>
                Demo Site
              </Heading>
              <ul>
                <li>
                  Designed & Built a{" "}
                  <Links href="https://muibook.com" target="_blank" rel="noopener noreferrer">
                    demo site
                  </Links>{" "}
                  for browsing and testing components
                </li>
                <li>
                  Includes <b>technical documentation</b> for each component, <b>usage examples</b>, and{" "}
                  <b>live demos</b>
                </li>
                <li>
                  Serves as a central hub with links to <b>UX Guidelines</b>, <b>Create App</b>, <b>Theming</b>, and
                  more
                </li>
                <li>
                  Features real-world compositions to demonstrate <b>UI recipes</b> and <b>best practices</b>
                </li>
                <li>
                  Enables testing of <b>theming</b>, <b>dark mode</b>, and other system tokens in context
                </li>
                <li>
                  Validates the system internally and helps <b>uncover gaps</b> through <b>dogfooding</b>
                </li>
                <li>
                  Builds upon the baked-in <b>accessibility</b> of the components to create an inclusive demo site
                  experience
                </li>
                <li>
                  Accessibility was considered when composing the demo site experience, including{" "}
                  <b>keyboard navigation</b>, <b>ARIA roles</b> usage on components, and clear <b>focus indicators</b>
                </li>
                <li>
                  Demonstrates a commitment to <b>inclusive design</b>, even in internal tools, and showcases the
                  ability to embed accessibility into component-based systems
                </li>
              </ul>
              <Box mt="large" mb="larger">
                <Image dark src={ImageBrand} alt="Demo website" />
              </Box>
            </Box>
            <Box>
              <Heading variant="medium">
                {" "}
                <span role="img" aria-label="bricks" style={{ marginRight: "var(--spacing-tiny)" }}>
                  🛠️
                </span>
                Create App
              </Heading>
              <ul>
                <li>
                  Provided a <b>starter template</b> using Vite for quickly building lightweight apps with Muibook
                  components
                </li>
                <li>
                  Demonstrates <b>framework-agnostic</b> usage via vanilla JS or React setups
                </li>
              </ul>
              <Box mt="large" mb="larger">
                <Image dark src={ImageThree} alt="Create App Starter Kit" caption="create.muibook.com" />
              </Box>
            </Box>
            <Box>
              <Heading variant="medium">
                {" "}
                <span role="img" aria-label="bricks" style={{ marginRight: "var(--spacing-tiny)" }}>
                  📦
                </span>
                NPM Package
              </Heading>
              <ul>
                <li>
                  All <b>30 agnostic components</b>
                </li>
                <li>
                  <b>Reset</b>, <b>Base</b> and <b>Token</b> CSS styles
                </li>
                <li>Type definitions</li>
                <li>
                  AI <b>prompt</b> and <b>keyword</b> tooling
                </li>
                <li>
                  React helpers to <b>prevent TS errors</b> when using Web Components
                </li>
                <li>
                  <b>part-map util</b> to enable part styling on selected components
                </li>
              </ul>
            </Box>
            <Box>
              <Heading variant="medium">
                {" "}
                <span role="img" aria-label="bricks" style={{ marginRight: "var(--spacing-tiny)" }}>
                  🎨
                </span>
                Styling System w/ CSS Parts
              </Heading>
              <p>
                In the past, I experimented with React-based approaches like styled-system for styling components. While
                it felt useful at the time, I wouldn’t reach for it again. Instead, I now prefer encapsulated styles —
                applying layout classes directly to the host element. For cases where internal layout adjustments are
                needed, I introduced CSS Parts. This approach lets me extend and adapt components cleanly, which proved
                especially helpful when building composed components and the demo site.
              </p>
              <ul>
                <li>
                  Added a <b>part-helper</b> mode to select components for <b>custom styling</b> support
                </li>
                <li>
                  Introduced a structured <b>::part override strategy</b> that makes overrides <b>clear</b> and{" "}
                  <b>intentional</b>
                </li>
                <li>
                  Currently supported components include{" "}
                  <Links href="https://muibook.com/#/link" target="_blank" rel="noopener noreferrer">
                    Link
                  </Links>
                  ,{" "}
                  <Links href="https://muibook.com/#/button" target="_blank" rel="noopener noreferrer">
                    Button
                  </Links>
                  ,{" "}
                  <Links href="https://muibook.com/#/stack" target="_blank" rel="noopener noreferrer">
                    Stack
                  </Links>
                  ,{" "}
                  <Links href="https://muibook.com/#/grid" target="_blank" rel="noopener noreferrer">
                    Grid
                  </Links>
                  ,{" "}
                  <Links href="https://muibook.com/#/body" target="_blank" rel="noopener noreferrer">
                    Body
                  </Links>
                  , and{" "}
                  <Links href="https://muibook.com/#/select" target="_blank" rel="noopener noreferrer">
                    Select
                  </Links>
                  , with opportunities to extend support based on use cases
                </li>
                <li>
                  CSS Parts are structured into:{" "}
                  <Links href="https://muibook.com/#/text-part-selectors" target="_blank" rel="noopener noreferrer">
                    Text
                  </Links>
                  ,{" "}
                  <Links href="https://muibook.com/#/spacing-part-selectors" target="_blank" rel="noopener noreferrer">
                    Spacing
                  </Links>
                  ,{" "}
                  <Links href="https://muibook.com/#/layout-part-selectors" target="_blank" rel="noopener noreferrer">
                    Layout
                  </Links>
                  , and{" "}
                  <Links href="https://muibook.com/#/visual-part-selectors" target="_blank" rel="noopener noreferrer">
                    Visual
                  </Links>
                </li>
                <li>
                  Read more in{" "}
                  <Links
                    href="https://medium.com/@trilfordm/using-the-css-part-attribute-in-my-web-component-library-4365d10439b6"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {" "}
                    my article on CSS Parts
                  </Links>
                </li>
              </ul>
              <Image dark src={ImageFour} alt="Part selectors" caption="Part selectors" />
            </Box>
            <Box>
              <Heading variant="medium">
                {" "}
                <span role="img" aria-label="bricks" style={{ marginRight: "var(--spacing-tiny)" }}>
                  🖼️
                </span>
                Figma System
              </Heading>
              <ul>
                <li>
                  Built <b>1:1 Figma components</b> to <b>match the coded components</b>
                </li>
                <li>
                  Maintains <b>consistent props and naming</b> between Figma and code for smoother collaboration
                </li>
                <li>
                  View the{" "}
                  <Links
                    href="https://www.figma.com/design/l0mt1lXu97XoHJCEdnrWLp/Mui-Design-System?node-id=0-1&t=8nkwHmdMRX4SAMil-1"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Figma UI Kit
                  </Links>{" "}
                  , a work in progress that will continue to evolve with ongoing updates planned beyond this publication
                </li>
              </ul>

              <Image dark src={Figma} alt="Figma UI Kit" caption="Figma UI Kit" />
            </Box>
            <Box>
              <Heading variant="medium">
                {" "}
                <span role="img" aria-label="bricks" style={{ marginRight: "var(--spacing-tiny)" }}>
                  📚
                </span>
                UX Guidelines
              </Heading>
              <p>
                <Links href="https://gurusuite.xyz/home" target="_blank" rel="noopener noreferrer">
                  Guru Guides
                </Links>{" "}
                is a tool I built to help design system teams, especially smaller ones, publish UX guidelines directly
                inside Figma so designers can access them when needed, reducing context switching.
              </p>

              <Image
                dark
                src={Guidelines}
                alt="Access direct guidelines within Figma"
                caption="Access direct guidelines within Figma"
              />

              <p>
                It provides ready-made templates and CMS integration to streamline content creation. I used it to
                accelerate the Muibook guidelines and dogfood the product, showing how it can be a cost-effective
                alternative to platforms like Zeroheight, especially since it is free.
              </p>
              <ul>
                <li>
                  Used Guru Guides{" "}
                  <Links
                    href="https://medium.com/@trilfordm/guidelines-at-your-fingertips-848bdaeee0ee"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    (Read article)
                  </Links>{" "}
                  to serve the <b>UX guidelines</b> in Figma via a <b>dedicated plugin</b>
                </li>
                <li>
                  <b>Whitelabelled</b> Guru Guides to integrate Muibook Web Components, replacing React components to
                  demonstrate flexibility and <b>dogfood the system</b>
                </li>
                <li>
                  The UX Guidelines at{" "}
                  <Links href="https://guides.muibook.com/" target="_blank" rel="noopener noreferrer">
                    guides.muibook.com
                  </Links>{" "}
                  are actively in development as of this publication and will continue to evolve with the project
                </li>
              </ul>

              <Image dark src={ImageFive} alt="Muibook Guidelines" caption="guidelines.muibook.com" />

              <p style={{ marginTop: "var(--spacing-x-medium)" }}>
                The <strong>UX Guidelines</strong> have been translated into <strong>Prompt-based Structures</strong>,
                turning each section, rule, and example into modular <strong>AI-readable Patterns</strong>.
              </p>

              <p>
                This allows <strong>Design Guidance</strong> to be consumed by both <strong>Humans & Machines</strong>,
                ensuring the system is future-ready as AI-assisted design tools become more accessible.
              </p>

              <p>
                The groundwork enables a seamless bridge between authored <strong>UX Documentation</strong> and
                automated design or code generation workflows.
              </p>
            </Box>
            <Box>
              <Heading variant="medium">
                {" "}
                <span role="img" aria-label="bricks" style={{ marginRight: "var(--spacing-tiny)" }}>
                  🤖
                </span>
                AI Integration
              </Heading>
              <p>
                The MUI Playground dogfoods the CDN build published to NPM, allowing Web Components to load dynamically
                within the Agent’s sandboxed modal. This enables a lightweight, zero-install method for making the
                design system available in runtime and AI-driven environments
              </p>
              <ul>
                <li>
                  Built a <b>MUI Playground</b> that leverages AI to explore and interact with design components
                </li>
                <li>
                  Architected and exported Design System Guidelines as <b>AI-ready Keywords</b> and <b>Prompts</b> via
                  NPM to support no-code prototyping tools.
                </li>
                <li>
                  Empowered AI tools with a deep understanding of the design system to enable <b>smarter</b>,{" "}
                  <b>context-aware usage</b>
                </li>
                <li>
                  Launched the live AI Playground (Alpha) at{" "}
                  <Links href="https://play.muibook.com/" target="_blank" rel="noopener noreferrer">
                    play.muibook.com
                  </Links>{" "}
                  as a proof of concept
                </li>
                <li>
                  Additional defined prompts and broader component coverage are planned, with continued iteration
                  expected after publication
                </li>
              </ul>
              <Image dark src={ImageSix} alt="Playground" caption="play.muibook.com" />
            </Box>
          </Stack>
        </Box>
        <Box>
          <Heading variant="large">What’s Next?</Heading>
          <p>
            Muibook is a personal project, but it’s also an invitation. If you want to explore it, test it, or
            contribute, you’re more than welcome. It’s not perfect, and it’s not finished. But it’s real, and it’s
            growing.
          </p>
          <p>
            This is my vision of what a connected, modern design system can look like, and the kind of system I’d want
            to use every day. I’ll be taking some time to reflect on what I’ve built, and I’ll continue to iterate and
            expand Muibook as the system evolves.
          </p>
        </Box>
        <Box>
          <Heading variant="medium">Explore Muibook</Heading>
          <p>
            <Links href="https://muibook.com/" target="_blank" rel="noopener noreferrer">
              <span aria-label="muibook" role="img">
                🌐
              </span>{" "}
              muibook.com
            </Links>{" "}
            — The technical component docs and examples
          </p>
          <p>
            <Links href="https://guides.muibook.com/" target="_blank" rel="noopener noreferrer">
              <span aria-label="muibook" role="img">
                🌐
              </span>{" "}
              guides.muibook.com
            </Links>{" "}
            — Web access to the UX Guidelines for the Mui components
          </p>
          <p>
            <Links href="https://create.muibook.com/" target="_blank" rel="noopener noreferrer">
              <span aria-label="muibook" role="img">
                🌐
              </span>{" "}
              create.muibook.com
            </Links>{" "}
            — Kickstart your project with a lightweight scaffold built on Vite
          </p>
          <p>
            <Links href="https://play.muibook.com/" target="_blank" rel="noopener noreferrer">
              <span aria-label="muibook" role="img">
                🌐
              </span>{" "}
              play.muibook.com
            </Links>{" "}
            — The live AI playground (Alpha)
          </p>
          <p>
            <Links
              href="https://www.npmjs.com/package/@muibook/components?activeTab=readme"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span aria-label="npmjs" role="img">
                📦
              </span>{" "}
              npmjs.com/@muibook/components
            </Links>{" "}
            — Install and use the components
          </p>
          <p>
            <Links href="https://github.com/michaeltrilford/muibook" target="_blank" rel="noopener noreferrer">
              <span aria-label="github" role="img">
                💻
              </span>{" "}
              github.com/michaeltrilford/muibook
            </Links>{" "}
            — Source, issues, and contributions welcome
          </p>
        </Box>
      </Stack>
    </BlogLayout>
  </CaseStudiesCard>
);

export default Muibook;
